import React from 'react';
import withFeatureState from './Navigation/state';
import { useAppContext } from 'components/v2/AppContext';

// Redux State Mappers

import { useTheme, styled } from '@mui/material/styles';

// mui
import Drawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Hidden from '@mui/material/Hidden';
import IconButton from '@mui/material/IconButton';

// icons
import MenuIcon from '@mui/icons-material/Menu';

// Components
import CompanyLogo from 'components/v2/PageElements/CompanyLogo';
import Navigation from './Navigation';

import AccountMenu from './AccountMenu';

// styles
const drawerWidth = 240;
const appbarHeight = 80;

const Root = styled(Box)({
  display: 'flex',
  height: 'auto !important',
});

const DrawerContent = styled(Box)(({ theme }) => ({
  ...theme.mixins.toolbar,
  paddingTop: appbarHeight + 5,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'isAdminContext',
})(({ theme, isAdminContext }) => ({
  zIndex: theme.zIndex.appBar,
  height: appbarHeight,
  ...(isAdminContext && {
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[900],
  }),
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: `${theme.zIndex.appBar - 1} !important`,
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.background.paper,
    width: drawerWidth,
  },
}));

const DrawerPaper = (theme) => ({
  width: drawerWidth,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
});

const DrawerContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    width: drawerWidth,
    flexShrink: 0,
  },
  zIndex: `${theme.zIndex.appBar - 1} !important`,
}));

const Content = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingBottom: theme.spacing(10),
  paddingTop: appbarHeight + 10,
  background: theme.palette.background.default,
  flex: 1,
  height: '100%',
}));

const HeaderTitle = styled(Box)({
  padding: '0 0 0 2rem',
});

const HeaderAction = styled(Box)({
  marginLeft: 'auto',
});

const HeaderText = styled(Typography)({
  paddingTop: '0',
  paddingBottom: '0',
});

const HeaderSubtitle = styled(Typography)({
  paddingTop: '0',
});

const HeaderContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
});

const LogoBox = styled(Box)({
  marginTop: 10,
});

const Component = ({ history, title = 'Compliance', children }) => {
  const theme = useTheme();

  const { isAdminContext } = useAppContext();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <DrawerContent>
      <Navigation />
    </DrawerContent>
  );

  return (
    <Root>
      <CssBaseline />
      <AppBar position="fixed" isAdminContext={isAdminContext}>
        <Toolbar>
          <MenuButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </MenuButton>
          <HeaderContainer>
            <LogoBox>
              <CompanyLogo />
            </LogoBox>

            <HeaderTitle>
              <HeaderText variant="h6" noWrap>
                {title}
              </HeaderText>
              {!isAdminContext && (
                <HeaderSubtitle variant="subtitle1" noWrap>
                  {'My Compliance'}
                </HeaderSubtitle>
              )}
              {isAdminContext && (
                <HeaderSubtitle variant="subtitle1" noWrap>
                  {'Management Console'}
                </HeaderSubtitle>
              )}
            </HeaderTitle>
            <HeaderAction>
              <AccountMenu />
            </HeaderAction>
          </HeaderContainer>
        </Toolbar>
      </AppBar>
      <DrawerContainer component="nav" aria-label="mailbox folders">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Hidden smUp implementation="css">
          <StyledDrawer
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            PaperProps={{
              sx: DrawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawer}
          </StyledDrawer>
        </Hidden>
        <Hidden xsDown implementation="css">
          <StyledDrawer
            PaperProps={{
              sx: DrawerPaper,
            }}
            variant="permanent"
            open
          >
            {drawer}
          </StyledDrawer>
        </Hidden>
      </DrawerContainer>
      <Content component="main">{children}</Content>
    </Root>
  );
};

export default withFeatureState(Component);
